import { GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../../graphql/activity";
import { useQuery } from "@apollo/client";
import React, { useCallback, useContext, useMemo } from "react";
import apolloClient from "../../configs/graphql.config";
import ActivityListView, { Activity, ActivityType } from "../../components/ActivityListView";
import { useI18n } from "../../hooks/i18n-hooks";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { getActivityNavigationInfo, getActivityTitle } from "../../hooks/activities";
import { useNavigation } from "@react-navigation/native";
import EmptyScreen from "../../components/EmptyScreen";
import NoTodosImage from "../../assets/images/no-activities-image.svg";
import ScreenCard from "../ScreenCard";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import Loading from "../../components/Loading";
import { buildErrorHeadband } from "../ScreenCardHeadband";

type TodosScreenProps = StackScreenProps<RootStackParamList>;

const UserTodosScreen = () => {
    const navigation = useNavigation<TodosScreenProps["navigation"]>();
    const { liftProfile, refreshUnreadProfilesActivities } = useContext(
        ProfileSwitcherManagerContext,
    );

    const i18n = useI18n();
    const {
        data: todoFeed,
        loading,
        refetch: todoRefresh,
        error,
    } = useQuery(GET_ACTIVITY_TODO_FEED, {
        context: { liftProfile },
    });

    const getActionName = useCallback(
        (activityName: string) => {
            return i18n.t(`activities.actions.${activityName}`);
        },
        [i18n],
    );
    const activityTodos = useMemo(() => {
        const result = (todoFeed?.getActivityTodoFeed || [])
            .map((todo) => {
                return {
                    id: todo.id,
                    name: todo.activityName,
                    activityKey: todo.activityKey,
                    title: getActivityTitle(todo.title),
                    text: todo.text,
                    createdAt: todo.createdAt,
                    badgeUrl: todo.badgeUrl,
                    priority: todo.priority,
                    at: new Date(todo.createdAt),
                    actionText: getActionName(todo.activityName),
                    type: ActivityType.todo,
                    sourceName: todo.sourceName,
                };
            })
            .sort((a, b) => {
                if (a.priority !== b.priority) {
                    return b.priority - a.priority;
                }
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
        return result;
    }, [getActionName, todoFeed?.getActivityTodoFeed]);

    const onSelectTodo = useCallback(
        (activity: Activity) => {
            const navInfo = getActivityNavigationInfo(activity.name, activity.activityKey);
            if (navInfo) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                navigation.navigate(navInfo);
            }
        },
        [navigation],
    );

    const refetch = useCallback(async () => {
        void apolloClient.refetchQueries({
            include: [GET_ALL_PROFILES_ACTIVITIES_INFO],
        });
        return Promise.all([todoRefresh(), refreshUnreadProfilesActivities()]);
    }, [refreshUnreadProfilesActivities, todoRefresh]);

    const isInitialLoading = loading && !todoFeed;

    return (
        <>
            <ScreenCard
                refresh={refetch}
                isContentContainerFlex={!error && (activityTodos.length === 0 || isInitialLoading)}
                headband={
                    error
                        ? buildErrorHeadband(i18n.t("screens.user.todos.cannot_load_todos"))
                        : undefined
                }
            >
                {isInitialLoading ? (
                    <Loading />
                ) : activityTodos.length === 0 ? (
                    <EmptyScreen
                        image={NoTodosImage}
                        text={i18n.t("screens.user.todos.no_activity")}
                    />
                ) : (
                    <ActivityListView
                        activities={activityTodos}
                        onSelectActivity={onSelectTodo}
                        // shadowActivityId={pressingActivity}
                    />
                )}
            </ScreenCard>
        </>
    );
};

export default UserTodosScreen;
